import ContentContainer from '@/features/shared/ContentContainer';
import DetailContent from '@/features/shared/DetailContent';
import Image from '@/features/shared/Image';
import { MuiContainer, MuiTab, MuiTabs, MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import TwoColumnContent from '@/features/shared/TwoColumnContent';
import { SpacingSizes, Theme } from '@/utils/constants';
import { resolveTheme } from '@/utils/resolvers';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { ChangeEvent, forwardRef, memo, PropsWithChildren, useMemo, useState } from 'react';
import { PersonaTabsProps } from './PersonaTabs.interface';
import useStyles from './PersonaTabs.styles';

const a11yProps = (index: any) => {
  return {
    id: `persona-tab-${index}`,
    'aria-controls': `persona-tabpanel-${index}`,
  };
};

interface TabPanelProps {
  children?: any;
  index?: any;
  value?: any;
  panelName?: string;
  classes?: any;
}

const mapVariantToProps = ({ variant }: any) => {
  if (/^image$/.test(variant)) {
    return {
      type: 'featured',
    };
  }

  return {};
};

const mapVariantToDetailContentClassName =
  (classes: any) =>
  ({ variant }: any) => {
    if (/^rtc$/.test(variant)) {
      return classes.rtc;
    }

    if (/^link$/.test(variant)) {
      return classes.link;
    }

    return '';
  };

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, panelName, classes, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`persona-${panelName}-${index}`}
      aria-labelledby={`persona-tab-${index}`}
      className={classes.tabPanelAnimated}
      {...other}
    >
      {children}
    </div>
  );
};

const RightDetailContentPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme,
  children,
  tabColor,
}: PropsWithChildren<{
  classes: any;
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
  tabColor?: string;
}>) => {
  const rootClasses = clsx(classes.tabPanel, 'right-column', `bg-color-${tabColor}`);
  return (
    <div className={rootClasses}>
      {children}
      {tabs.map(({ tabDetailContent = {} }, i) => {
        const { mediaContent = [] } = tabDetailContent;
        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            classes={classes}
          >
            <div className={classes.media}>
              <DetailContent
                content={mediaContent}
                theme={theme}
                mapVariantToProps={mapVariantToProps}
              />
            </div>
          </TabPanel>
        );
      })}
    </div>
  );
};

const LeftDetailContentPanels = ({
  classes,
  tabs = [],
  value = 0,
  theme,
  children,
}: PropsWithChildren<{
  classes: any;
  tabs: TabContentType[];
  value: number;
  theme: ThemeType;
}>) => {
  return (
    <div className={clsx(classes.tabPanel, 'left-column')}>
      {children}
      {tabs.map(({ tabDetailContent = {} }, i) => {
        const { detailContent = [], heading, superHeading } = tabDetailContent;

        return (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={`tabPanel_${i}`}
            value={value}
            panelName="tabpanel"
            index={i}
            classes={classes}
          >
            {superHeading && (
              <MuiTypography variant="overline" component="h5" className={classes.superheading}>
                {superHeading}
              </MuiTypography>
            )}
            {heading && (
              <RichText
                customStyles
                theme={theme}
                className={clsx(classes.heading, 'gtm-headline')}
                content={heading}
              />
            )}
            <DetailContent
              content={detailContent}
              theme={theme}
              mapVariantToClassName={mapVariantToDetailContentClassName(classes)}
            />
          </TabPanel>
        );
      })}
    </div>
  );
};

const CustomTabComponent = forwardRef((props: any, ref: any) => {
  const { children, title, tabIndex, ...tabProps } = props;
  const { text: tabText, logo } = title;
  const hasLogo = useMemo(() => logo && logo.original, [logo]);
  const hasContent = useMemo(() => hasLogo || tabText, [hasLogo, tabText]);

  return (
    <>
      {hasContent && (
        <div {...tabProps} ref={ref}>
          {logo && (
            <div className="image">
              <Image image={logo} type="card" size="contain" />
            </div>
          )}
          {tabText && (
            <MuiTypography className="heading" variant="subtitle1" component="h2">
              {tabText}
            </MuiTypography>
          )}
        </div>
      )}
    </>
  );
});

const PersonaTabs = ({
  tabGroupDescription,
  tabGroupTitle,
  theme = Theme.dark,
  tabs = [],
  containerProps,
  tabColor,
  contentButtons,
}: Br.PropsWithContentButtons<PersonaTabsProps>) => {
  const tabTheme = resolveTheme('bg-color')({ rawVal: tabColor });
  const displayInset = useMemo(
    () =>
      tabColor !== containerProps?.topBackgroundColor ||
      tabColor !== containerProps?.bottomBackgroundColor,
    [containerProps?.bottomBackgroundColor, containerProps?.topBackgroundColor, tabColor],
  );
  const classes = useStyles({});
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [value, setValue] = useState(0);
  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const rootClasses = clsx(classes.root, `${theme}-theme`, 'large');
  const rootTabsClasses = clsx(
    classes.content,
    `${tabTheme}-theme`,
    `bg-color-${tabColor}`,
    displayInset && 'display-inset',
  );
  const rootTabClasses = clsx(classes.tab, 'gtm-tab');
  const rootTabPanelClasses = clsx(
    classes.twoColumns,
    `${tabTheme}-theme`,
    `bg-color-${tabColor}`,
    displayInset && 'display-inset',
  );
  const areTabsScrollable = React.useMemo(() => tabs.length > 5 || isMobile, [tabs, isMobile]);

  return (
    <ContentContainer
      {...containerProps}
      topBackgroundColor={containerProps?.topBackgroundColor}
      bottomBackgroundColor={containerProps?.bottomBackgroundColor}
      mobileTopBackgroundColor={tabColor}
      mobileBottomBackgroundColor={tabColor}
      mobileTopSpacing={SpacingSizes.small}
      mobileBottomSpacing={SpacingSizes.small}
      data-component-variant="persona-tabs"
    >
      <MuiContainer className={rootClasses}>
        {(tabGroupTitle || tabGroupDescription) && (
          <div className={classes.header}>
            {tabGroupTitle && (
              <MuiTypography variant="h2" className={`${classes.headerText} gtm-headline-main`}>
                {tabGroupTitle}
              </MuiTypography>
            )}
            {tabGroupDescription && (
              <MuiTypography
                variant="h6"
                className={classes.headerText}
                dangerouslySetInnerHTML={{ __html: tabGroupDescription }}
              />
            )}
          </div>
        )}

        <div className={rootTabsClasses}>
          {contentButtons?.content}

          {tabs.length && (
            <MuiTabs
              orientation="horizontal"
              variant="scrollable"
              scrollButtons={areTabsScrollable ? 'on' : 'off'}
              value={value}
              onChange={handleChange}
              aria-label="Persona MuiTabs"
              className={classes.tabs}
              TabIndicatorProps={{ className: classes.tabIndicator }}
            >
              {tabs.map(({ tabTitle }, i) => (
                <MuiTab
                  // eslint-disable-next-line react/no-array-index-key
                  key={`tab_${i}`}
                  title={tabTitle}
                  {...a11yProps(i)}
                  component={CustomTabComponent}
                  className={rootTabClasses}
                />
              ))}
            </MuiTabs>
          )}
          <TwoColumnContent
            className={rootTabPanelClasses}
            columnRatio="35-65"
            leftColumn={
              <LeftDetailContentPanels
                theme={tabTheme}
                tabs={tabs}
                value={value}
                classes={classes}
              />
            }
            rightColumn={
              <RightDetailContentPanels
                theme={tabTheme}
                tabs={tabs}
                value={value}
                classes={classes}
                tabColor={tabColor}
              />
            }
          />
        </div>
      </MuiContainer>
    </ContentContainer>
  );
};

export default memo(PersonaTabs);
